import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatTableDataSource } from '@angular/material';
import { AppConfig } from '@config/app.config';
import { GeneralsService } from '@core/services/generals.service';
import { TransfersService } from '@core/services/transfers.service';
import { TransfersListComponent } from '@modules/transfers/transfers-list/transfers-list.component';
import { Constants } from '@shared/class/constants.class';
import { CustomerTransfer } from '@shared/interfaces/customer';
import { Paginator, SearchCustomer, SearchReference, TransferCustomer } from '@shared/interfaces/transfer';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-transfers-customer-list',
  templateUrl: './transfers-customer-list.component.html'
})
export class TransfersCustomerListComponent implements OnInit, OnDestroy {
  public constants = new Constants();

  public customer: CustomerTransfer;

  public location = 'transfersCustomer';
  public actions: Array<string> = [
    this.appConfig.ACTIONS.detail.id,
    this.appConfig.ACTIONS.edit.id
  ];
  public dataSource: MatTableDataSource<TransferCustomer>;
  public selection = new SelectionModel<TransferCustomer>(true, []);
  public transfersList: Array<TransferCustomer> = [];
  public transfer: TransferCustomer;
  public loadingCustomer = true;
  public loadingTransfers = true;
  public rol = 'Rol';
  public rolName = '';
  public rolIdentify = '';
  public paginator: Paginator = {
    page: 0,
    start: 1,
    end: 10,
    total: 0
  };

  constructor(
    public appConfig: AppConfig,
    public generalsService: GeneralsService,
    private transfersService: TransfersService,
    public dialogRef: MatDialogRef<TransfersCustomerListComponent>,
    @Inject(MAT_DIALOG_DATA) private data
  ) {
    this.constants.displayedColumns = [
      'dateSend',
      'reference',
      'identifyCustomer',
      'customer',
      'value',
      'status',
      this.appConfig.ACTIONS.detail.id
    ];
    const result = this.generalsService.getActions(
      this.constants.displayedColumns,
      this.actions,
      this.location
    );

    this.constants.displayedColumns = result.displayedColumns;
    this.constants.displayedActions = result.displayedActions;
  }

  ngOnInit() {
    this.constants.loading = true;
    this.loadingCustomer = true;
    this.loadingTransfers = true;
    this.constants.elements = true;
    this.constants.title = this.appConfig.LANG.transfers.listTransfers;
    const rolForm = this.data.form.rol;
    if (rolForm === this.appConfig.OWNER) {
      this.rol = this.appConfig.LANG.transfers.owner;
      this.rolName =
        this.appConfig.LANG.transfers.name +
        this.appConfig.LANG.transfers.beneficiary;
      this.rolIdentify =
        this.appConfig.LANG.transfers.identify +
        this.appConfig.LANG.transfers.beneficiary;
    } else if (rolForm === this.appConfig.BENEFICIARY) {
      this.rol = this.appConfig.LANG.transfers.beneficiary;
      this.rolName =
        this.appConfig.LANG.transfers.name +
        this.appConfig.LANG.transfers.owner;
      this.rolIdentify =
        this.appConfig.LANG.transfers.identify +
        this.appConfig.LANG.transfers.owner;
    } else {
      this.constants.error = true;
      this.constants.loading = false;
    }
    this.processCustomerResponse();

    this.generalsService.transfer$
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((result: any) => {
        if (result) {
          this.passPage('actual');
        }
      });
  }

  ngOnDestroy() {
    this.constants.destroy$.next(true);
    this.constants.destroy$.unsubscribe();
  }

  private processCustomerResponse() {
    this.constants.loading = false;
    this.loadingCustomer = false;
    const result = this.data.transfers;
    this.customer = {
      typeIdentify: result.meta['document-type-name'],
      identify: result.meta['document-number'],
      name: result.meta['fullname']
    };
    this.processTransfersCustomerResponse(result);
  }

  private processTransfersCustomerResponse(result: any) {
    this.paginator.total = result.meta['total-items'];
    this.paginator.end =
      this.paginator.total < 10 ? this.paginator.total : this.paginator.end;
    this.transfersList = [];
    result.data.forEach((element: any) => {
      const attributes = element.attributes;
      const total = parseFloat(attributes['amount']);

      this.transfer = {
        detail: element.id,
        dateSend: attributes['sent-date'],
        reference: element.id,
        value: total,
        identifyCustomer: attributes['document-number'],
        customer: attributes['fullname'],
        status: attributes['status-name']
      };
      this.transfersList.push(this.transfer);
    });
    if (this.transfersList.length) {
      this.dataSource = new MatTableDataSource(this.transfersList);
      this.constants.filter = this.dataSource.filteredData;
      this.selection.clear();
    } else {
      this.constants.elements = false;
    }
    this.loadingTransfers = false;
  }

  passPage(position: any) {
    this.loadingTransfers = true;
    this.setPaginator(position);
    const formData: SearchCustomer = this.data.form;
    formData.page = this.paginator.page;

    this.transfersService
      .getItemSearch(formData)
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          this.processTransfersCustomerResponse(result);
          this.loadingTransfers = false;
        },
        error => {
          this.constants.errorText = this.generalsService.showError(error);
          this.constants.error = true;
          this.loadingTransfers = false;
        }
      );
  }

  openTransfer(reference: any) {
    const formData: SearchReference = {
      channel: this.data.form.channel,
      reference: reference,
      user: this.data.form.user
    };

    this.generalsService.openTransfer(formData, TransfersListComponent);
  }

  setPaginator(position: any) {
    switch (position) {
      case 'next':
        if (this.paginator.page < Math.trunc(this.paginator.total / 10)) {
          this.paginator.page++;
          this.paginator.start = this.paginator.start + 10;
          this.paginator.end =
            this.paginator.end + 10 > this.paginator.total
              ? this.paginator.total
              : this.paginator.end + 10;
        }
        break;
      case 'prev':
        if (this.paginator.page > 0) {
          this.paginator.page--;
          this.paginator.start = this.paginator.start - 10;
          this.paginator.end = (this.paginator.page + 1) * 10;
        }
        break;
      case 'end':
        this.paginator.page = Math.trunc(this.paginator.total / 10);
        const sub = this.paginator.total - (this.paginator.page * 10 + 1);
        this.paginator.start = this.paginator.total - sub;
        this.paginator.end = this.paginator.total;
        break;
      case 'start':
        this.paginator.page = 0;
        this.paginator.start = 1;
        this.paginator.end = 10;
        break;
    }
  }

  close() {
    this.dialogRef.close(300);
  }
}
